import { useState, useMemo, useCallback } from "react"

export type SortDirection = "asc" | "desc"

export interface SortState<K> {
  sortKey: K
  sortDirection: SortDirection
}

export interface UseSortableDataResult<T, K> {
  sortedData: T[]
  sortKey: K
  sortDirection: SortDirection
  handleSort: (key: K) => void
}

/**
 * A generic hook for sorting data with togglable direction.
 *
 * @param data - The array of data to sort
 * @param defaultSortKey - The initial sort key
 * @param comparators - An object mapping sort keys to comparator functions
 * @returns Sorted data and sort controls
 */
export function useSortableData<T, K extends string>(
  data: T[],
  defaultSortKey: NoInfer<K>,
  comparators: Record<K, (a: T, b: T) => number>
): UseSortableDataResult<T, K> {
  const [sortKey, setSortKey] = useState<K>(defaultSortKey)
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

  const sortedData = useMemo(() => {
    const comparator = comparators[sortKey]
    if (!comparator) return data

    return [...data].sort((a, b) => {
      const comparison = comparator(a, b)
      return sortDirection === "asc" ? comparison : -comparison
    })
  }, [data, sortKey, sortDirection, comparators])

  const handleSort = useCallback((key: K) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDirection("asc")
    }
  }, [sortKey])

  return { sortedData, sortKey, sortDirection, handleSort }
}
